import React, { useState, useRef, useEffect } from "react";
import dayjs from "dayjs";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronRight as ChevronForward,
} from "lucide-react";

const weekDays = ["일", "월", "화", "수", "목", "금", "토"];

function getMonthMatrix(year, month) {
  const firstDay = dayjs(`${year}-${month}-01`);
  const startDay = firstDay.day();
  const daysInMonth = firstDay.daysInMonth();
  const matrix = [];
  let day = 1 - startDay;

  for (let i = 0; i < 6; i++) {
    const row = [];
    for (let j = 0; j < 7; j++, day++) {
      row.push(day > 0 && day <= daysInMonth ? day : null);
    }
    matrix.push(row);
  }
  return matrix;
}

const years = Array.from({ length: 8 }, (_, i) => 2022 + i);
const months = Array.from({ length: 12 }, (_, i) => i + 1);

const SeparateWheelPicker = ({
  currentYear,
  currentMonth,
  onYearChange,
  onMonthChange,
  onClose,
}) => {
  const [yearScrollTop, setYearScrollTop] = useState(0);
  const [monthScrollTop, setMonthScrollTop] = useState(0);
  const [tempYear, setTempYear] = useState(currentYear);
  const [tempMonth, setTempMonth] = useState(currentMonth);
  const [isYearScrolling, setIsYearScrolling] = useState(false);
  const [isMonthScrolling, setIsMonthScrolling] = useState(false);
  const yearScrollRef = useRef(null);
  const monthScrollRef = useRef(null);
  const yearScrollTimeoutRef = useRef(null);
  const monthScrollTimeoutRef = useRef(null);
  const itemHeight = 32;
  const visibleItems = 7;
  const containerHeight = itemHeight * visibleItems;

  const PREGNANCY_DAYS = 280;
  const today = dayjs();
  const minSelectableDate = today.startOf('day');
  const maxSelectableDate = today.add(PREGNANCY_DAYS, 'day');

  // 연도가 선택 가능한지 확인
  const isYearSelectable = (year) => {
    const startOfYear = dayjs(`${year}-01-01`);
    const endOfYear = dayjs(`${year}-12-31`);
    
    // 해당 연도에 선택 가능한 날짜가 하나라도 있는지 확인
    return (startOfYear.isBefore(maxSelectableDate.add(1, 'day')) && 
            endOfYear.isAfter(minSelectableDate.subtract(1, 'day')));
  };

  // 월이 선택 가능한지 확인
  const isMonthSelectable = (year, month) => {
    const startOfMonth = dayjs(`${year}-${month}-01`);
    const endOfMonth = startOfMonth.endOf('month');
    
    // 해당 월에 선택 가능한 날짜가 하나라도 있는지 확인
    return (startOfMonth.isBefore(maxSelectableDate.add(1, 'day')) && 
            endOfMonth.isAfter(minSelectableDate.subtract(1, 'day')));
  };

  // 가장 가까운 아이템으로 스냅하는 함수
  const snapToNearestItem = (scrollTop, scrollRef, items, setTempValue, setScrollTop) => {
    const centerIndex = Math.round(scrollTop / itemHeight);
    const clampedIndex = Math.max(0, Math.min(centerIndex, items.length - 1));
    const targetScroll = clampedIndex * itemHeight;
    
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: targetScroll,
        behavior: 'smooth'
      });
    }
    setScrollTop(targetScroll);
    setTempValue(items[clampedIndex]);
  };

  useEffect(() => {
    const targetIndex = years.findIndex((year) => year === tempYear);
    if (targetIndex >= 0) {
      const targetScroll = targetIndex * itemHeight;

      if (yearScrollRef.current) {
        yearScrollRef.current.scrollTop = targetScroll;
        setYearScrollTop(targetScroll);
      }
    }
  }, [tempYear, itemHeight]);

  useEffect(() => {
    const targetIndex = months.findIndex((month) => month === tempMonth);
    if (targetIndex >= 0) {
      const targetScroll = targetIndex * itemHeight;

      if (monthScrollRef.current) {
        monthScrollRef.current.scrollTop = targetScroll;
        setMonthScrollTop(targetScroll);
      }
    }
  }, [tempMonth, itemHeight]);

  const handleYearScroll = (e) => {
    const newScrollTop = e.target.scrollTop;
    setYearScrollTop(newScrollTop);
    setIsYearScrolling(true);

    if (yearScrollTimeoutRef.current) {
      clearTimeout(yearScrollTimeoutRef.current);
    }

    yearScrollTimeoutRef.current = setTimeout(() => {
      setIsYearScrolling(false);
      snapToNearestItem(newScrollTop, yearScrollRef, years, setTempYear, setYearScrollTop);
    }, 150);
  };

  const handleMonthScroll = (e) => {
    const newScrollTop = e.target.scrollTop;
    setMonthScrollTop(newScrollTop);
    setIsMonthScrolling(true);

    if (monthScrollTimeoutRef.current) {
      clearTimeout(monthScrollTimeoutRef.current);
    }

    monthScrollTimeoutRef.current = setTimeout(() => {
      setIsMonthScrolling(false);
      snapToNearestItem(newScrollTop, monthScrollRef, months, setTempMonth, setMonthScrollTop);
    }, 150);
  };

  useEffect(() => {
    return () => {
      if (yearScrollTimeoutRef.current) {
        clearTimeout(yearScrollTimeoutRef.current);
      }
      if (monthScrollTimeoutRef.current) {
        clearTimeout(monthScrollTimeoutRef.current);
      }
    };
  }, []);

  const getItemStyle = (index, scrollTop) => {
    const centerIndex = scrollTop / itemHeight;
    const distance = index - centerIndex;
    const absDistance = Math.abs(distance);

    if (absDistance > visibleItems / 2) {
      return {
        opacity: 0,
        transform: "rotateX(0deg) scale(0.8)",
        pointerEvents: "none",
      };
    }

    const maxRotation = 45;
    const rotation = distance * (maxRotation / (visibleItems / 2));
    const opacity = Math.max(0.2, 1 - absDistance * 0.3);
    const scale = Math.max(0.75, 1 - absDistance * 0.1);
    const translateY = distance * 1.5;

    return {
      opacity,
      transform: `perspective(1000px) rotateX(${rotation}deg) scale(${scale}) translateY(${translateY}px)`,
      transformOrigin: "center center",
      transition: "all 0.15s ease-out",
    };
  };

  const handleYearClick = (targetYear, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // 스크롤 중이면 클릭 무시
    if (isYearScrolling) return;
    
    // 선택 불가능한 연도면 무시
    if (!isYearSelectable(targetYear)) return;
    
    setTempYear(targetYear);
  };

  const handleMonthClick = (targetMonth, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // 스크롤 중이면 클릭 무시
    if (isMonthScrolling) return;
    
    // 선택 불가능한 월이면 무시
    if (!isMonthSelectable(tempYear, targetMonth)) return;
    
    setTempMonth(targetMonth);
  };

  const handlePickerClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // 선택된 연/월이 모두 유효한지 확인
    if (!isYearSelectable(tempYear) || !isMonthSelectable(tempYear, tempMonth)) {
      return; // 유효하지 않으면 완료 처리하지 않음
    }
    
    onYearChange(tempYear, tempMonth);
    onClose();
  };

  // 현재 선택된 연/월이 모두 선택 가능한지 확인
  const canComplete = isYearSelectable(tempYear) && isMonthSelectable(tempYear, tempMonth);

  return (
    <div className="relative" style={{ height: containerHeight + 60 }}>
      <div className="absolute inset-0 pointer-events-none z-10">
        <div className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-white to-transparent" />
        <div className="absolute bottom-12 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent" />
      </div>

      <div
        className="absolute left-4 right-4 bg-gray-200/50 rounded-lg pointer-events-none z-5"
        style={{
          top: `${itemHeight * 3}px`,
          height: `${itemHeight}px`,
        }}
      />

      <div className="flex justify-center" style={{ height: containerHeight }}>
        <div className="relative" style={{ width: "24%" }}>
          <div
            ref={yearScrollRef}
            className="h-full overflow-y-scroll"
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              WebkitScrollbar: "none",
            }}
            onScroll={handleYearScroll}
          >
            <div style={{ height: itemHeight * 3 }} />

            {years.map((year, index) => {
              const yearSelectable = isYearSelectable(year);
              return (
                <div
                  key={year}
                  className={`flex items-center justify-center text-[22px] cursor-pointer select-none ${
                    isYearScrolling ? 'pointer-events-none' : ''
                  } ${!yearSelectable ? 'opacity-40' : ''}`}
                  style={{
                    height: itemHeight,
                    ...getItemStyle(index, yearScrollTop),
                  }}
                  onClick={(e) => handleYearClick(year, e)}
                >
                  <span className={`font-medium ${!yearSelectable ? 'text-gray-400' : ''}`}>
                    {year}년
                  </span>
                </div>
              );
            })}

            <div style={{ height: itemHeight * 3 }} />
          </div>
        </div>

        <div className="relative" style={{ width: "24%" }}>
          <div
            ref={monthScrollRef}
            className="h-full overflow-y-scroll"
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              WebkitScrollbar: "none",
            }}
            onScroll={handleMonthScroll}
          >
            <div style={{ height: itemHeight * 3 }} />

            {months.map((month, index) => {
              const monthSelectable = isMonthSelectable(tempYear, month);
              return (
                <div
                  key={month}
                  className={`flex items-center justify-center text-[22px] cursor-pointer select-none ${
                    isMonthScrolling ? 'pointer-events-none' : ''
                  } ${!monthSelectable ? 'opacity-40' : ''}`}
                  style={{
                    height: itemHeight,
                    ...getItemStyle(index, monthScrollTop),
                  }}
                  onClick={(e) => handleMonthClick(month, e)}
                >
                  <span className={`font-medium ${!monthSelectable ? 'text-gray-400' : ''}`}>
                    {month}월
                  </span>
                </div>
              );
            })}

            <div style={{ height: itemHeight * 3 }} />
          </div>
        </div>
      </div>

      <div className="flex mt-[30px]">
        <button
          type="button"
          className={`w-full py-[15px] rounded-[8px] font-semibold text-[16px] transition-colors ${
            canComplete 
              ? "bg-[#FEC009] text-white hover:bg-[#E5AB08]" 
              : "bg-[#E9EAEE] text-[#CCCCCC] cursor-not-allowed"
          }`}
          onClick={handlePickerClose}
          disabled={!canComplete}
        >
          완료
        </button>
      </div>
    </div>
  );
};

const BottomSheetCalendar = ({ open, onClose, value, onChange }) => {
  const [viewDate, setViewDate] = useState(value ? dayjs(value) : dayjs());
  const [selected, setSelected] = useState(value ? dayjs(value) : null);
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const matrix = getMonthMatrix(viewDate.year(), viewDate.month() + 1);

  // 0일 기준으로 280일까지 선택 가능한 날짜 범위 계산
  const PREGNANCY_DAYS = 280;
  const today = dayjs(); 
  const minSelectableDate = today.startOf('day'); 
  const maxSelectableDate = today.add(PREGNANCY_DAYS, 'day'); 

  const isDateSelectable = (date) => {
    if (!date) return false;
    // 0일(오늘) 이상 280일 이하 범위 체크
    const isFromToday = date.isAfter(minSelectableDate) || date.isSame(minSelectableDate, 'day'); 
    const isWithin280Days = date.isBefore(maxSelectableDate) || date.isSame(maxSelectableDate, 'day'); 
    
    return isFromToday && isWithin280Days;
  };

  const handleYearChange = (year, month = null) => {
    if (month !== null) {
      const newViewDate = viewDate.year(year).month(month - 1);
      setViewDate(newViewDate);
    } else {
      setViewDate(viewDate.year(year));
    }
  };

  const handleMonthChange = (month) => {
    setViewDate(viewDate.month(month - 1)); 
  };

  // 피커 닫기
  const handlePickerClose = () => {
    setIsPickerOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/30"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[900px] bg-white rounded-t-[30px] px-[23px] pt-[40px] pb-[50px]"
        style={{ minHeight: 400 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4 md:mb-6 px-[10px]">
          <button
            type="button"
            className={`flex items-center gap-1 font-semibold text-[20px] md:text-[24px] transition-colors ${
              isPickerOpen ? "text-[#FEC009]" : "text-black"
            }`}
            onClick={() => {
              setIsPickerOpen((v) => !v);
            }}
          >
            {viewDate.format("YYYY년 M월")}
            {isPickerOpen ? (
              <ChevronDown
                size={20}
                className="transition-transform text-[#FEC009]"
              />
            ) : (
              <ChevronForward size={20} className="transition-transform" />
            )}
          </button>

          {!isPickerOpen && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setViewDate(viewDate.subtract(1, "month"))}
                aria-label="이전달"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                type="button"
                onClick={() => setViewDate(viewDate.add(1, "month"))}
                aria-label="다음달"
              >
                <ChevronRight size={24} />
              </button>
            </div>
          )}
        </div>

        {isPickerOpen && (
          <div className="mb-6">
            <SeparateWheelPicker
              currentYear={viewDate.year()}
              currentMonth={viewDate.month() + 1}
              onYearChange={handleYearChange}
              onMonthChange={handleMonthChange}
              onClose={handlePickerClose}
            />
          </div>
        )}

        {!isPickerOpen && (
          <>
            <div className="grid grid-cols-7 text-[12px] md:text-[18px] text-center text-[#888888] mb-2">
              {weekDays.map((d) => (
                <div key={d}>{d}</div>
              ))}
            </div>

            <div className="grid grid-cols-7 text-[17px] md:text-[24px] text-center gap-y-2 mb-6">
              {matrix.map((row, i) =>
                row.map((day, j) => {
                  const currentDate = day ? viewDate.date(day) : null;
                  const isSelected =
                    selected &&
                    day &&
                    selected.isSame(currentDate, "day");
                  const isSelectable = currentDate ? isDateSelectable(currentDate) : false;
                  
                  return (
                    <div key={i + "-" + j}>
                      {day ? (
                        <button
                          type="button"
                          className={`w-10 h-10 md:w-14 md:h-14 rounded-full transition-colors ${
                            isSelected
                              ? "bg-[#FEF8E8] text-[#FEC009] font-semibold"
                              : isSelectable
                              ? "text-black hover:bg-gray-100"
                              : "text-gray-300 cursor-not-allowed bg-gray-50"
                          }`}
                          onClick={() => {
                            if (isSelectable) {
                              setSelected(currentDate);
                            }
                          }}
                          disabled={!isSelectable}
                        >
                          {day}
                        </button>
                      ) : (
                        <span>&nbsp;</span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}

        {!isPickerOpen && (
          <button
            type="button"
            className="w-full py-[15px] rounded-[8px] bg-[#FEC009] text-white font-semibold text-[16px]"
            onClick={() => {
              if (selected) {
                onChange(selected.format("YYYY-MM-DD"));
                onClose();
              }
            }}
          >
            확인
          </button>
        )}
      </div>
    </div>
  );
};

export default BottomSheetCalendar;
